import { Moment } from "moment";
import { Address } from "../../address/address.model";
import { PaymentMethod } from "../../core/payment-method.model";
import { PricingMode } from "../../core/pricing-mode.model";
import { InvoiceBase } from "../../invoicing/abstract/invoice-base.model";
import { Email } from "../../messaging/email.model";
import { InvoiceContact } from "../contact.model";
import { SalesDocumentTemplate } from "../template.model";
import { FkField, OmitField } from "src/app/@shared/decorators/api-field.decorator";
import { StatusEnum } from "src/app/@shared/enums/status.enum";
import { TaxExemptionSourceEnum } from "src/app/@shared/enums/tax-exemption-source.enum";

export abstract class SalesInvoiceBase extends InvoiceBase {

    private _bankDetails!: string;
    private _billingAddress!: Address;
    private _chequeLabel!: string;
    private _contact!: InvoiceContact;
    private _deliveryAddress!: Address;
    private _draftEmail!: Email;
    private _ecoTaxGrossAmount!: number;
    private _ecoTaxNetAmount!: number;
    private _lastReminderDate!: Moment;
    private _logo!: string;
    private _note!: string;
    private _paymentMethods!: PaymentMethod[];
    private _pricingMode!: PricingMode;
    private _referenceTmp!: string;
    private _reminderCount!: number;
    private _sentEmails!: Email[];
    private _subject!: string;
    private _taxExemptionSource!: TaxExemptionSourceEnum | null;
    private _template!: SalesDocumentTemplate;
    private _useDeliveryAddress!: boolean;
    private _vendorAddress!: Address;

    public hasPaymentMethod(paymentMethod: string): boolean {
        return this.paymentMethods.some((pm: PaymentMethod) => pm.code === paymentMethod);
    }

    public get bankDetails(): string {
        return this._bankDetails;
    }

    public set bankDetails(value: any) {
        this._setValue('_bankDetails', value, 'string');
    }

    @OmitField()
    public get billingAddress(): Address {
        return this._billingAddress;
    }

    public set billingAddress(value: any) {
        this._setValue('_billingAddress', value, 'Address');
    }

    public get chequeLabel(): string {
        return this._chequeLabel;
    }

    public set chequeLabel(value: any) {
        this._setValue('_chequeLabel', value, 'string');
    }

    @OmitField()
    public get contact(): InvoiceContact {
        return this._contact;
    }

    public set contact(value: any) {
        this._setValue('_contact', value, 'InvoiceContact');
    }

    @OmitField()
    public get deliveryAddress(): Address {
        return this._deliveryAddress;
    }

    public set deliveryAddress(value: any) {
        this._setValue('_deliveryAddress', value, 'Address');
    }

    /**
     * Sur un document de vente, le libellé comptable n'est posé qu'à la validation : tant
     * que le document est en brouillon, le seul texte saisi est son objet (ticket #941).
     *
     * Le repli sur le libellé couvre les documents sans objet — une facture de vente
     * importée, dont le libellé est saisi directement.
     */
    public override get displayLabel(): string {
        return this.hasStatus(StatusEnum.Draft) && !!this.subject ? this.subject : this.label;
    }

    @OmitField()
    public get draftEmail(): Email {
        return this._draftEmail;
    }

    public set draftEmail(value: any) {
        this._setValue('_draftEmail', value, 'Email');
    }

    @OmitField()
    public get ecoTaxGrossAmount(): number {
        return this._ecoTaxGrossAmount;
    }

    public set ecoTaxGrossAmount(value: any) {
        this._setValue('_ecoTaxGrossAmount', value, 'float');
    }

    @OmitField()
    public get ecoTaxNetAmount(): number {
        return this._ecoTaxNetAmount;
    }

    public set ecoTaxNetAmount(value: any) {
        this._setValue('_ecoTaxNetAmount', value, 'float');
    }

    /** Le document a-t-il déjà fait l'objet d'au moins une relance envoyée ? */
    public get hasReminders(): boolean {
        return this.reminderCount > 0;
    }

    /**
     * Date d'envoi de la dernière relance (annotation serveur, lecture seule).
     */
    @OmitField()
    public get lastReminderDate(): Moment {
        return this._lastReminderDate;
    }

    public set lastReminderDate(value: any) {
        this._setValue('_lastReminderDate', value, 'date');
    }

    @OmitField()
    public get logo(): string {
        return this._logo;
    }

    public set logo(value: any) {
        this._setValue('_logo', value, 'string');
    }

    public get note(): string {
        return this._note;
    }

    public set note(value: any) {
        this._setValue('_note', value, 'string');
    }

    @FkField('code')
    public get paymentMethods(): PaymentMethod[] {
        return this._paymentMethods;
    }

    public set paymentMethods(value: any) {
        this._setValue('_paymentMethods', value, 'PaymentMethod');
    }

    @FkField('code')
    public get pricingMode(): PricingMode {
        return this._pricingMode;
    }

    public set pricingMode(value: any) {
        this._setValue('_pricingMode', value, 'PricingMode');
    }

    @OmitField()
    public get referenceTmp(): string {
        return this._referenceTmp;
    }

    public set referenceTmp(value: any) {
        this._setValue('_referenceTmp', value, 'string');
    }

    /**
     * Nombre de relances réellement envoyées (annotation serveur, lecture seule).
     */
    @OmitField()
    public get reminderCount(): number {
        return this._reminderCount;
    }

    public set reminderCount(value: any) {
        this._setValue('_reminderCount', value, 'integer');
    }

    /** Libellé de l'indicateur de relance : nombre de relances et date de la dernière */
    public get remindersText(): string {
        if (!this.hasReminders) return '';

        let plural: string = this.reminderCount > 1 ? 's' : '';
        let text: string = `${this.reminderCount} relance${plural} envoyée${plural}`;

        return this.lastReminderDate
            ? `${text} — dernière le ${this.lastReminderDate.format('DD/MM/YYYY')}`
            : text;
    }

    @OmitField()
    public get sentEmails(): Email[] {
        return this._sentEmails;
    }

    public set sentEmails(value: any) {
        this._setValue('_sentEmails', value, 'Email');
    }

    /**
     * Objet du document, imprimé sur le PDF. Distinct du libellé comptable (`label`),
     * qui en est initialisé à la validation puis reste corrigeable (cf. ticket #941).
     */
    public get subject(): string {
        return this._subject;
    }

    public set subject(value: any) {
        this._setValue('_subject', value, 'string');
    }

    /**
     * Ce qui rend ce document **non taxable**, ou `null` si rien ne l'impose — #1089.
     *
     * **Calculé et servi par le serveur**, jamais renvoyé (`@OmitField`). Deux valeurs
     * seulement à ce niveau — `territory` et `partner` : la troisième cause, le groupe
     * d'articles réglé à 0 %, ne vaut que pour une ligne et se lit sur elle.
     *
     * C'est ce champ qui verrouille la ventilation d'une facture saisie à l'entête, qui
     * n'a pas de lignes. Il remplace le couple `isTaxExemptIn()` / `isForeignPartnerIn()`,
     * qui recopiait la règle du serveur et dépendait de deux relations que la projection
     * masquait de toute façon (`partner/country`).
     */
    @OmitField()
    public get taxExemptionSource(): TaxExemptionSourceEnum | null {
        return this._taxExemptionSource;
    }

    public set taxExemptionSource(value: any) {
        this._setValue('_taxExemptionSource', value, 'string');
    }

    @FkField()
    public get template(): SalesDocumentTemplate {
        return this._template;
    }

    public set template(value: any) {
        this._setValue('_template', value, 'SalesDocumentTemplate');
    }

    public get useDeliveryAddress(): boolean {
        return this._useDeliveryAddress;
    }

    public set useDeliveryAddress(value: any) {
        this._setValue('_useDeliveryAddress', value, 'boolean');
    }

    @OmitField()
    public get vendorAddress(): Address {
        return this._vendorAddress;
    }

    public set vendorAddress(value: any) {
        this._setValue('_vendorAddress', value, 'Address');
    }

}